import {useEffect, useRef, useState} from 'react'

import Colors from '@utils/Colors.js'
import L from '../leaflet.js'
import {isTypingTarget, Point} from '../geometry.js'
import {
    activeSelectionShape,
    isMacPlatform,
    pressSelectionKey,
    releaseSelectionKey,
    selectionShapeOfKey,
    SelectionShape
} from '../selectionKeys.js'
import useLatest from './useLatest.js'

export type {SelectionShape}

export interface MapSelectionOptions {
    /** The shape switched on with the toolbar, null when the map pans */
    mode: SelectionShape | null
    /** Called with the selected area when the mouse is released, at least 3 points */
    onSelect: (polygon: Point[]) => void
    color?: string
}

const mac = isMacPlatform()

/**
 * Draws a selection area while the mouse is held down on the map and selecting is on: with the shape of the toolbar,
 * or of a held key (Shift a box, Ctrl a lasso, on a Mac Cmd as well). The last pressed held key wins over the toolbar.
 * The map doesn't pan while selecting and shows a crosshair. A key typed in a field doesn't count and leaving the
 * window releases the keys, so they can't get stuck. Returns the shape it selects with, null when it doesn't.
 */
export default function useMapSelection(map: L.Map | null, {mode, onSelect, color = Colors.backgroundDark}: MapSelectionOptions) {
    // The held selection keys, in the order they went down
    const [held, setHeld] = useState<string[]>([])
    const active = activeSelectionShape(mode, held, mac)
    const latest = useLatest({active, onSelect, color})
    const cancelRef = useRef<() => void>(() => {})

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (selectionShapeOfKey(event.key, mac) && !isTypingTarget(event.target)) {
                setHeld((held) => pressSelectionKey(held, event.key))
            }
        }

        const onKeyUp = (event: KeyboardEvent) => {
            setHeld((held) => releaseSelectionKey(held, event.key))
        }

        const onBlur = () => {
            setHeld([])
            cancelRef.current()
        }

        document.addEventListener('keydown', onKeyDown)
        document.addEventListener('keyup', onKeyUp)
        window.addEventListener('blur', onBlur)

        return () => {
            document.removeEventListener('keydown', onKeyDown)
            document.removeEventListener('keyup', onKeyUp)
            window.removeEventListener('blur', onBlur)
        }
    }, [])

    useEffect(() => {
        if (!map) {
            return
        }

        const container = map.getContainer()

        if (active) {
            map.dragging.disable()
            container.style.cursor = 'crosshair'
        } else {
            cancelRef.current()
            map.dragging.enable()
            container.style.cursor = ''
        }
    }, [map, active])

    useEffect(() => {
        if (!map) {
            return
        }

        let points: L.LatLng[] = []
        let drawing: SelectionShape | null = null
        let area: L.Polygon | null = null
        let start: L.Point | null = null
        // Whether the mouse moved away from where it went down, else releasing it is a plain click
        let moved = false

        const container = map.getContainer()

        // The click that ends a selection would also hit the marker under the mouse (and toggle the orders it just
        // selected) or the map, so it is swallowed before it reaches them
        const swallowClick = (event: MouseEvent) => {
            event.stopPropagation()
            stopSwallowing()
        }

        // Also on the next mousedown: releasing the mouse outside the map gives it no click
        const stopSwallowing = () => {
            container.removeEventListener('click', swallowClick, true)
            document.removeEventListener('mousedown', stopSwallowing, true)
        }

        const cancel = () => {
            drawing = null
            points = []
            start = null
            moved = false
            area?.remove()
            area = null
        }

        const onMouseDown = (event: L.LeafletMouseEvent) => {
            const {button, ctrlKey} = event.originalEvent

            // Ctrl+click is a right click on a Mac, which some browsers report as button 2
            if (!latest.current.active || !(button === 0 || (mac && ctrlKey && button === 2))) {
                return
            }

            drawing = latest.current.active
            points = [event.latlng]
            start = event.containerPoint
            // Dragging over the page would select its text
            document.getSelection()?.removeAllRanges()
        }

        const onMouseMove = (event: L.LeafletMouseEvent) => {
            if (!drawing) {
                return
            }

            // A few pixels is a shaking hand on a click, not a selection
            if (!moved && start.distanceTo(event.containerPoint) < 3) {
                return
            }

            moved = true

            if (drawing === 'box') {
                // The corners of the rectangle between the start and the mouse
                const start = points[0]
                const corner = event.latlng
                points = [start, L.latLng(start.lat, corner.lng), corner, L.latLng(corner.lat, start.lng)]
            } else {
                // Skip points closer than a few pixels to the previous one, they add nothing but work
                if (map.latLngToContainerPoint(points[points.length - 1]).distanceTo(event.containerPoint) < 3) {
                    return
                }

                points.push(event.latlng)
            }

            if (area) {
                area.setLatLngs(points)
            } else {
                area = L.polygon(points, {color: latest.current.color, weight: 1, fillOpacity: 0.1, interactive: false}).addTo(map)
            }
        }

        // On the document, so releasing the mouse outside the map also ends the lasso
        const onMouseUp = () => {
            if (!drawing) {
                return
            }

            const polygon = points.map(({lat, lng}) => ({lat, lng}))
            const wasMoved = moved
            cancel()

            // Without moving it stays a click, e.g. on a marker
            if (!wasMoved) {
                return
            }

            container.addEventListener('click', swallowClick, true)
            document.addEventListener('mousedown', stopSwallowing, true)

            if (polygon.length >= 3) {
                latest.current.onSelect(polygon)
            }
        }

        // No context menu for a Ctrl+click on a Mac while selecting, it would end the lasso
        const onContextMenu = (event: MouseEvent) => {
            if (latest.current.active) {
                event.preventDefault()
            }
        }

        cancelRef.current = cancel
        container.addEventListener('contextmenu', onContextMenu)
        map.on('mousedown', onMouseDown)
        map.on('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)

        return () => {
            container.removeEventListener('contextmenu', onContextMenu)
            map.off('mousedown', onMouseDown)
            map.off('mousemove', onMouseMove)
            document.removeEventListener('mouseup', onMouseUp)
            stopSwallowing()
            cancel()
            cancelRef.current = () => {}
        }
    }, [map])

    return active
}
